/* Alaman Empresarial — interacciones (sin dependencias). */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Imágenes diferidas del inicio (snippet al-defer-img): se cargan a 300 px de la pantalla.
     En un carrusel se cargan todas juntas para que no aparezcan vacías al deslizar. */
  const showImg = (img) => {
    if (!img.hasAttribute('data-al-defer')) return;
    img.removeAttribute('data-al-defer');
    if (img.dataset.alSrcset) img.srcset = img.dataset.alSrcset;
    if (img.dataset.alSrc) img.src = img.dataset.alSrc;
  };
  const deferObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const track = e.target.closest('.al-carousel__track');
          (track ? track.querySelectorAll('img[data-al-defer]') : [e.target]).forEach((img) => { showImg(img); deferObserver.unobserve(img); });
        });
      }, { rootMargin: '300px 0px' })
    : null;
  document.querySelectorAll('img[data-al-defer]').forEach((img) => (deferObserver ? deferObserver.observe(img) : showImg(img)));

  /* Formulario enviado: el aviso queda al centro de la pantalla (otra vez al terminar de cargar,
     porque las secciones de arriba pueden cambiar de alto al dibujarse). */
  const formOk = document.querySelector('[data-al-form-ok]');
  if (formOk) {
    const showOk = () => formOk.scrollIntoView({ block: 'center' });
    showOk();
    formOk.focus({ preventScroll: true });
    window.addEventListener('load', () => setTimeout(showOk, 150), { once: true });
  }

  /* Aparición al hacer scroll */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revealObserver.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -10% 0px' })
    : null;
  const initReveal = (root = document) => {
    root.querySelectorAll('.al-reveal:not(.is-in)').forEach((el) => {
      if (revealObserver && !reduced) revealObserver.observe(el); else el.classList.add('is-in');
    });
  };

  /* Contadores animados */
  const countObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { runCount(e.target); countObserver.unobserve(e.target); } });
      }, { threshold: 0.25 })
    : null;
  function runCount(el) {
    const target = parseFloat(el.dataset.count);
    if (isNaN(target) || reduced) return;
    const decimals = (el.dataset.count.split('.')[1] || '').length;
    const prefix = el.dataset.prefix || '';
    const suffix = el.dataset.suffix || '';
    const start = performance.now();
    const dur = 2000;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const v = target * (1 - Math.pow(1 - p, 3));
      el.textContent = prefix + v.toLocaleString('es-MX', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
  const initCounters = (root = document) => {
    root.querySelectorAll('[data-count]').forEach((el) => countObserver && countObserver.observe(el));
  };

  /* Carrusel con scroll-snap */
  class AlCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.al-carousel__track');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dotsWrap = this.querySelector('.al-carousel__dots');
      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.update()); }, { passive: true });
      this.ro = new ResizeObserver(() => { this.buildDots(); this.update(); });
      this.ro.observe(this.track);
      const secs = parseInt(this.dataset.autoplay, 10);
      if (secs > 0 && !reduced && !window.Shopify?.designMode) {
        this.timer = setInterval(() => { if (!this.paused) this.go(1, true); }, secs * 1000);
        ['mouseenter', 'focusin', 'touchstart'].forEach((ev) => this.addEventListener(ev, () => (this.paused = true), { passive: true }));
        ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => (this.paused = false)));
      }
    }
    disconnectedCallback() { clearInterval(this.timer); this.ro && this.ro.disconnect(); }
    get pageWidth() { return this.track.clientWidth; }
    get pages() { return Math.max(1, Math.ceil((this.track.scrollWidth - 4) / this.pageWidth)); }
    get page() { return Math.round(this.track.scrollLeft / this.pageWidth); }
    go(dir, loop) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (loop && dir > 0 && this.track.scrollLeft >= max - 4) { this.track.scrollTo({ left: 0 }); return; }
      this.track.scrollBy({ left: dir * this.pageWidth });
    }
    buildDots() {
      if (!this.dotsWrap) return;
      const n = this.pages;
      if (this.dotsWrap.children.length === n) return;
      this.dotsWrap.innerHTML = '';
      for (let i = 0; i < n; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'al-carousel__dot';
        b.setAttribute('aria-label', `Ir a la página ${i + 1}`);
        b.addEventListener('click', () => this.track.scrollTo({ left: i * this.pageWidth }));
        this.dotsWrap.appendChild(b);
      }
      this.dotsWrap.hidden = n < 2;
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 4;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 4;
      const nav = this.querySelector('.al-carousel__nav');
      if (nav) nav.hidden = max <= 4;
      if (this.dotsWrap) [...this.dotsWrap.children].forEach((d, i) => d.setAttribute('aria-current', i === this.page ? 'true' : 'false'));
    }
  }
  if (!customElements.get('al-carousel')) customElements.define('al-carousel', AlCarousel);

  /* Hero con transición de desvanecido */
  class AlHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.al-hero__slide')];
      this.dots = [...this.querySelectorAll('.al-carousel__dot')];
      this.i = 0;
      if (this.slides.length < 2) return;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.i - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.i + 1, true));
      this.dots.forEach((d, n) => d.addEventListener('click', () => this.show(n, true)));
      this.secs = parseInt(this.dataset.autoplay, 10) || 0;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      document.addEventListener('shopify:block:select', (e) => {
        const n = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (n > -1) { this.stop(); this.show(n); }
      });
    }
    disconnectedCallback() { this.stop(); }
    start() { this.stop(); if (this.secs > 0 && !reduced && !window.Shopify?.designMode) this.timer = setInterval(() => this.show(this.i + 1), this.secs * 1000); }
    stop() { clearInterval(this.timer); }
    show(n, user) {
      const len = this.slides.length;
      this.i = (n + len) % len;
      this.slides.forEach((s, k) => {
        s.classList.toggle('is-active', k === this.i);
        s.setAttribute('aria-hidden', k === this.i ? 'false' : 'true');
        s.inert = k !== this.i;
      });
      this.dots.forEach((d, k) => d.setAttribute('aria-current', k === this.i ? 'true' : 'false'));
      if (user) this.start();
    }
  }
  if (!customElements.get('al-hero')) customElements.define('al-hero', AlHero);

  /* Menú móvil del encabezado */
  const initHeader = (root = document) => {
    root.querySelectorAll('[data-al-header]').forEach((header) => {
      if (header.dataset.ready) return;
      header.dataset.ready = '1';
      const btn = header.querySelector('[data-al-burger]');
      if (!btn) return;
      const setOpen = (open) => {
        header.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.querySelector('[data-open]').hidden = open;
        btn.querySelector('[data-close]').hidden = !open;
      };
      btn.addEventListener('click', () => setOpen(!header.classList.contains('is-open')));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
      window.matchMedia('(min-width: 990px)').addEventListener('change', (m) => m.matches && setOpen(false));
    });
  };

  /* Encabezado compacto al bajar */
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      document.querySelectorAll('[data-al-header]').forEach((h) => h.classList.toggle('is-scrolled', window.scrollY > 40));
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  /* "Leer más" en descripciones largas */
  const initClamp = (root = document) => {
    root.querySelectorAll('[data-al-clamp]').forEach((box) => {
      const btn = box.querySelector('.al-clamp__btn');
      if (!btn || btn.dataset.ready) return;
      btn.dataset.ready = '1';
      btn.addEventListener('click', () => {
        const open = box.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.firstChild.textContent = open ? 'Leer menos' : 'Leer más';
      });
    });
  };

  const init = (root) => { initReveal(root); initCounters(root); initHeader(root); initClamp(root); onScroll(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
